import { Fragment, useMemo, type ReactNode } from "react";
import classNames from "classnames";
import { MediaQuery } from "@outplayed/responsive";
import { TooltipContainer } from "@outplayed/tooltips";
import { asArray } from "@ugg/shared/utils/safe-coerce";
import {
  getClassicMasteryIconUrl,
  getClassicMasteryTreeBgUrl,
  type ClassicMasteryDisplay,
  type ClassicMasteryTree,
  type ClassicMasteryNode,
} from "@ugg/shared/hooks/classic";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

const MASTERY_TREE_COLOR: Record<string, string> = {
  offense: "#FF4655",
  defense: "#3273FA",
  utility: "#149C3A",
};
const MASTERY_CONNECTOR_OFF = "#33335a"; // dim — prerequisite not met

// Hex "#rrggbb" → "rgba(r, g, b, a)" — for the per-tree translucent header tint.
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Node grid geometry (px). Connectors run card-bottom → next card-top so they sit
// in the gap between nodes and never cross a node's rank label.
const MASTERY_ICON = 36;
// Row height sized so the tallest tree (6 old-mastery tiers) fits the 543px panel
// (header ~48 + node-area padding ~28 + 6 rows). Shorter trees leave bottom space.
const MASTERY_ROW_H = 78;
const MASTERY_NODE_PT = 6;
const MASTERY_CARD_BOTTOM = MASTERY_NODE_PT + MASTERY_ICON + 4 + 16; // pt + icon + gap + rank

export function ClassicMasteries(props: {
  masteryDisplay?: ClassicMasteryDisplay;
  selected: Record<number, number>;
  name: string;
  roleLabel: string;
}) {
  const { masteryDisplay, selected, name, roleLabel } = props;
  const trees = asArray(masteryDisplay?.trees);

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Masteries</span>
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span className="content-section_blurb">{`These are the best masteries for ${name} ${roleLabel}`}</span>
        </MediaQuery>
      </div>

      {trees.length === 0 ? (
        <div className="flex items-center justify-center h-[200px] text-lavender-50">Loading masteries…</div>
      ) : (
        // paddingTop 1 → the header's 18px margin-bottom + 1 = 19px label→trees
        // (margins would collapse, padding doesn't). gap-[19px] between panels.
        <div className="grid grid-cols-3 gap-[19px] max-md:grid-cols-1" style={{ paddingTop: 1 }}>
          {trees.map((tree) => (
            <MasteryTreeColumn key={tree.name} tree={tree} selected={selected} />
          ))}
        </div>
      )}
    </div>
  );
}

// One tree column: accent header + connected node grid. (The category summary
// now lives full-width below all three trees — see MasteryCategorySummary.)
function MasteryTreeColumn(props: { tree: ClassicMasteryTree; selected: Record<number, number> }) {
  const { tree, selected } = props;
  const color = MASTERY_TREE_COLOR[tree.type] || MASTERY_TREE_COLOR.offense;

  // Connector routing depends only on the tree's structure (node positions +
  // prerequisite links), so memoize on [tree]. Without this, changing the role
  // filter — which re-renders this module — rebuilds the position Map and the
  // connector list for all three trees on every render.
  const connectors = useMemo(() => {
    const posById = new Map<number, { row: number; col: number }>();
    asArray(tree.rows).forEach((row, r) =>
      asArray(row.masteries).forEach((node, c) => node && posById.set(node.id, { row: r, col: c })),
    );

    const result: Array<{ col: number; fromRow: number; toRow: number; prereqId: number }> = [];
    asArray(tree.rows).forEach((row) =>
      asArray(row.masteries).forEach((node) => {
        if (node?.requiresMastery && posById.has(node.requiresMastery)) {
          const from = posById.get(node.requiresMastery)!;
          const to = posById.get(node.id)!;
          // Every prerequisite link in the data is a same-column drop; draw a
          // vertical line between them.
          if (from.col === to.col) result.push({ col: to.col, fromRow: from.row, toRow: to.row, prereqId: node.requiresMastery });
        }
      }),
    );
    return result;
  }, [tree]);

  const total = masteryTreeTotal(tree, selected);

  return (
    // Tree panel — 314×543 with a #383864 border (figma). bg + border inline so
    // they apply reliably over any legacy styling.
    <div
      className="relative overflow-hidden rounded-[3px]"
      style={{ minHeight: 543, backgroundColor: "#11112a", border: "1px solid #383864" }}
    >
      {/* Background art (already tinted per tree), pinned to the top and faded into
          the panel bg toward the bottom so the lower rows stay legible. The source
          art's bottom frame falls in the faded/clipped region. */}
      <img
        src={getClassicMasteryTreeBgUrl(tree.type)}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute left-0 top-0 w-full select-none"
        style={{ zIndex: 0 }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ zIndex: 0, background: "linear-gradient(180deg, rgba(17, 17, 42, 0.5) 0%, #11112A 100%)" }}
      />
      {/* Header (312×50): white name (left) + total pts (right), 15/17/18/17
          padding, with a tree-colored 1px underline + a 20%-opacity tree-color
          wash so it stands out from the faded art below (figma Rectangle 1797). */}
      <div
        className="relative flex items-center justify-between"
        style={{
          zIndex: 1,
          height: 50,
          paddingTop: 15,
          paddingBottom: 18,
          paddingLeft: 17,
          paddingRight: 17,
          backgroundColor: hexToRgba(color, 0.2),
          borderBottom: `1px solid ${color}`,
          borderRadius: "2px 2px 0 0",
        }}
      >
        <span style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 14, lineHeight: 1, color: "#ffffff" }}>{tree.name}</span>
        <span
          className="text-right"
          style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 14, lineHeight: 1, color: "#ffffff" }}
        >
          {total} <span className="text-[11px] font-medium text-lavender-50">pts</span>
        </span>
      </div>

      {/* Node grid — 4 columns of 36px icons, 34px between them + 34px on each
          edge (columnGap + padding). Vertical: paddingTop 19 + node paddingTop 6
          = 25px header→first icon; ROW_H 78 (icon 36 + 42 gap) → 42px icon-to-icon;
          paddingBottom 6 → the last row's icon sits 42px above the panel bottom. */}
      <div
        className="relative grid"
        style={{
          zIndex: 1,
          gridTemplateColumns: "repeat(4, minmax(0,1fr))",
          columnGap: 34,
          paddingLeft: 34,
          paddingRight: 34,
          paddingTop: 19,
          paddingBottom: 6,
          gridAutoRows: `${MASTERY_ROW_H}px`,
        }}
      >
        {/* Connectors sit behind the nodes, in the gaps between cards; the tree
            accent color when the prerequisite is allocated, else dim. */}
        {connectors.map((con, i) => {
          const active = (selected[con.prereqId] || 0) > 0;
          const height = (con.toRow - con.fromRow) * MASTERY_ROW_H - (MASTERY_CARD_BOTTOM - MASTERY_NODE_PT);
          return (
            <div
              key={i}
              className="relative pointer-events-none"
              style={{ gridColumn: con.col + 1, gridRow: `${con.fromRow + 1} / ${con.toRow + 2}`, zIndex: 0 }}
            >
              <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full"
                style={{ top: MASTERY_CARD_BOTTOM, height, width: 2, backgroundColor: active ? color : MASTERY_CONNECTOR_OFF }}
              />
            </div>
          );
        })}

        {/* Node cells. */}
        {asArray(tree.rows).map((row, r) => (
          <Fragment key={r}>
            {asArray(row.masteries).map((node, c) =>
              node ? (
                <div
                  key={node.id}
                  className="flex justify-center"
                  style={{ gridColumn: c + 1, gridRow: r + 1, paddingTop: MASTERY_NODE_PT, zIndex: 1 }}
                >
                  <MasteryNode node={node} rank={selected[node.id] || 0} color={color} />
                </div>
              ) : null,
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// Sum of allocated ranks across a tree.
function masteryTreeTotal(tree: ClassicMasteryTree, selected: Record<number, number>): number {
  return asArray(tree.rows).reduce(
    (sum, row) => sum + asArray(row.masteries).reduce((s, m) => s + (m ? selected[m.id] || 0 : 0), 0),
    0,
  );
}

function MasteryNode(props: { node: ClassicMasteryNode; rank: number; color: string }) {
  const { node, rank, color } = props;
  const active = rank > 0;

  return (
    <TooltipContainer tooltipInfo={<MasteryNodeTooltip node={node} rank={rank} />}>
      <div className="flex flex-col items-center gap-[4px] cursor-default">
        {/* 36×36 icon; selected → 2px tree-color border (2px radius). The border
            is an overlay drawn ON TOP of the image so it covers the icon art's
            own light frame/corners, keeping the accent color crisp. (Kept at 2px
            from the edge — insetting it re-exposes the icon's own light border.) */}
        <div className="relative flex-none" style={{ width: MASTERY_ICON, height: MASTERY_ICON }}>
          <img
            src={getClassicMasteryIconUrl(node.id, active)}
            alt={node.name}
            loading="lazy"
            className={classNames("block", !active && "opacity-80")}
            style={{ width: MASTERY_ICON, height: MASTERY_ICON, borderRadius: 2 }}
          />
          {active && (
            <div className="absolute inset-0 pointer-events-none" style={{ borderRadius: 2, border: `2px solid ${color}` }} />
          )}
        </div>
        {/* Rank label — 28×16, radius 8, #0D0D28 bg; Inter 500 11px, white when
            allocated else #5C5C8E (figma). */}
        <div
          className="flex items-center justify-center"
          style={{
            width: 28,
            height: 16,
            borderRadius: 8,
            padding: 2,
            backgroundColor: "#0D0D28",
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: 11,
            lineHeight: "12px",
            color: active ? "#FFFFFF" : "#5C5C8E",
          }}
        >
          {rank}/{node.maxRank}
        </div>
      </div>
    </TooltipContainer>
  );
}

// Hover tooltip for a tree node — blue name, "Rank x / y", and the full ladder
// description with the active rank's value highlighted (see renderMasteryDescription).
// No icon, matching the rest of the site's tooltips.
function MasteryNodeTooltip(props: { node: ClassicMasteryNode; rank: number }) {
  const { node, rank } = props;
  return (
    <div className="max-w-[280px]">
      <div className="text-[14px] font-bold text-accent-blue-400">{node.name}</div>
      <div className="text-[12px] text-accent-gray-100">
        Rank {rank} / {node.maxRank}
      </div>
      <div className="mt-[8px] text-[13px] leading-[18px] text-white">{renderMasteryDescription(node.description, rank)}</div>
    </div>
  );
}

// ─── Toughest matchups (hardcoded Classic champ cards) ────────────────────────

// ─── Skill priority (classic skill data) ─────────────────────────────────────
//
// Skill Priority renders inside the Recommended panel's summoner-spells cell
// (see ClassicRecommendedPanel). The old standalone Skill Priority + Skill Path
// module was removed in QA, so only the 3-ability priority order survives — now
// sourced from the compact "mini" feed (useClassicChampionSkillsMini). The
// resolution logic lives in resolveSkillPriorityMini; the icon in ClassicSkillIcon.

function renderMasteryDescription(description: string, rank: number): ReactNode {
  if (typeof description !== "string") return null;
  const re = /(\d+(?:\.\d+)?%?(?:\s*\/\s*\d+(?:\.\d+)?%?)+)/;
  const m = description.match(re);
  if (!m || m.index === undefined) return description;

  const group = m[0];
  const parts = group.split("/").map((s) => s.trim());
  // rank 0 (unallocated node in a tooltip) → highlight nothing.
  const idx = rank > 0 ? Math.min(rank - 1, parts.length - 1) : -1;
  const before = description.slice(0, m.index);
  const after = description.slice(m.index + group.length);

  return (
    <>
      {before}
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && "/"}
          <span className={i === idx ? "text-accent-gold-400 font-bold" : undefined}>{p}</span>
        </Fragment>
      ))}
      {after}
    </>
  );
}
